import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const defaultInitialState = {
  isLoading: false,
  error: null,
  channelUrl: '',
  analysisData: null,
  lastAnalysisDate: null,
  isAutoLoadInProgress: false
};

const getInitialState = () => {
  try {
    const lastChannelUrl = localStorage.getItem('lastChannelUrl');
    const lastAnalysisDate = localStorage.getItem('lastChannelAnalysisDate');
    const cachedChannelData = localStorage.getItem('cachedChannelData');

    if (lastChannelUrl) {
      let parsedChannelData = null;

      if (cachedChannelData) {
        try {
          parsedChannelData = JSON.parse(cachedChannelData);
          
          const extractChannelIdentifier = (url) => {
            if (!url) return '';
            
            let path = url.replace(/^https?:\/\/(www\.)?youtube\.com\//, '');
            
            if (path.startsWith('@')) {
              return path.toLowerCase();
            } else if (path.startsWith('channel/')) {
              return path.replace('channel/', '').toLowerCase();
            } else if (path.startsWith('c/')) {
              return path.replace('c/', '').toLowerCase();
            } else if (path.startsWith('user/')) {
              return path.replace('user/', '').toLowerCase();
            }
            
            return path.toLowerCase();
          };

          const areChannelsEqual = (url1, url2) => {
            if (!url1 || !url2) return false;
            
            const id1 = extractChannelIdentifier(url1);
            const id2 = extractChannelIdentifier(url2);
            
            if (id1 === id2) {
              return true;
            }
            
            const isUsernameFormat1 = id1.startsWith('@');
            const isUsernameFormat2 = id2.startsWith('@');
            const isChannelIdFormat1 = id1.length > 20 && !isUsernameFormat1;
            const isChannelIdFormat2 = id2.length > 20 && !isUsernameFormat2;
            
            if ((isUsernameFormat1 && isChannelIdFormat2) || (isUsernameFormat2 && isChannelIdFormat1)) {
              return true;
            }
            
            return false;
          };
          
          if (parsedChannelData.channel_url && !areChannelsEqual(lastChannelUrl, parsedChannelData.channel_url)) {
            localStorage.removeItem('cachedChannelData');
            localStorage.removeItem('lastChannelAnalysisDate');
            parsedChannelData = null;
          }
          
          if (parsedChannelData && (parsedChannelData.analysis_successful !== true && parsedChannelData.analysis_successful !== undefined)) {
            localStorage.removeItem('cachedChannelData');
            localStorage.removeItem('lastChannelAnalysisDate');
            parsedChannelData = null;
          }
        } catch (e) {
          localStorage.removeItem('cachedChannelData');
          localStorage.removeItem('lastChannelAnalysisDate');
          parsedChannelData = null;
        }
      }

      return {
        ...defaultInitialState,
        channelUrl: lastChannelUrl,
        analysisData: parsedChannelData,
        lastAnalysisDate: parsedChannelData ? lastAnalysisDate : null,
        isAutoLoadInProgress: false
      };
    }
  } catch (e) {
    // Тихо обрабатываем ошибку
  }

  return defaultInitialState;
};

const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CHANNEL_URL: 'SET_CHANNEL_URL',
  SET_ANALYSIS_DATA: 'SET_ANALYSIS_DATA',
  RESET_ANALYSIS: 'RESET_ANALYSIS',
  SET_LAST_ANALYSIS_DATE: 'SET_LAST_ANALYSIS_DATE',
  CLEAR_HISTORY: 'CLEAR_HISTORY',
  SET_AUTO_LOAD_IN_PROGRESS: 'SET_AUTO_LOAD_IN_PROGRESS'
};

function channelAnalysisReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error,
      };
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isAutoLoadInProgress: false,
      };
    case ActionTypes.SET_CHANNEL_URL:
      return {
        ...state,
        channelUrl: action.payload,
      };
    case ActionTypes.SET_ANALYSIS_DATA:
      return {
        ...state,
        analysisData: action.payload,
        isLoading: false,
        error: null,
        isAutoLoadInProgress: false,
      };
    case ActionTypes.RESET_ANALYSIS:
      return {
        ...state,
        analysisData: null,
        error: null,
        isAutoLoadInProgress: false,
      };
    case ActionTypes.SET_LAST_ANALYSIS_DATE:
      return {
        ...state,
        lastAnalysisDate: action.payload,
      };
    case ActionTypes.CLEAR_HISTORY:
      return {
        ...defaultInitialState
      };
    case ActionTypes.SET_AUTO_LOAD_IN_PROGRESS:
      return {
        ...state,
        isAutoLoadInProgress: action.payload,
      };
    default:
      return state;
  }
}

export const ChannelAnalysisContext = createContext(defaultInitialState);

export function ChannelAnalysisProvider({ children }) {
  const [state, dispatch] = useReducer(channelAnalysisReducer, getInitialState());
  const { t } = useTranslation();

  const setLoading = (isLoading) => {
    dispatch({
      type: ActionTypes.SET_LOADING,
      payload: isLoading,
    });
  };

  const setError = (error) => {
    dispatch({
      type: ActionTypes.SET_ERROR,
      payload: error,
    });
  };

  const setChannelUrl = (url) => {
    if (url === state.channelUrl) {
      return;
    }

    if (url && url !== state.channelUrl) {
      localStorage.removeItem('cachedChannelData');
      localStorage.removeItem('lastChannelAnalysisDate');
      
      dispatch({
        type: ActionTypes.RESET_ANALYSIS,
      });
    }

    dispatch({
      type: ActionTypes.SET_CHANNEL_URL,
      payload: url,
    });

    if (url) {
      localStorage.setItem('lastChannelUrl', url);
    }
  };

  const setAutoLoadInProgress = (inProgress) => {
    dispatch({
      type: ActionTypes.SET_AUTO_LOAD_IN_PROGRESS,
      payload: inProgress,
    });
  };

  const setAnalysisData = (data) => {
    dispatch({
      type: ActionTypes.SET_ANALYSIS_DATA,
      payload: data,
    });

    if (data) {
      let timestampToUse;

      if (data.analysis_timestamp) {
        timestampToUse = data.analysis_timestamp;
      } else if (data.last_updated) {
        timestampToUse = data.last_updated;
      } else {
        timestampToUse = new Date().toISOString();
      }

      if (timestampToUse) {
        localStorage.setItem('lastChannelAnalysisDate', timestampToUse);
        dispatch({
          type: ActionTypes.SET_LAST_ANALYSIS_DATE,
          payload: timestampToUse
        });
      }

      const isAnalysisSuccessful = data.analysis_successful === true || 
                                   (!data.error && data.general_info?.title);

      if (isAnalysisSuccessful) {
        try {
          const dataToCache = {
            general_info: data.general_info,
            content: data.content,
            raw_data: data.raw_data,
            analysis: data.analysis,
            seo: data.seo,
            schedule: data.schedule,
            monetization_data: data.monetization_data,
            duration_metrics: data.duration_metrics,        
            content_metrics: data.content_metrics, 
            analysis_successful: isAnalysisSuccessful,
            analysis_timestamp: timestampToUse,
            cached_at: new Date().toISOString(),
            is_full_analysis: data.is_full_analysis,
            is_from_cache: data.is_from_cache,
            channel_url: data.channel_url || data.general_info?.url
          };

          localStorage.setItem('cachedChannelData', JSON.stringify(dataToCache));
          
        } catch (e) {
          localStorage.removeItem('cachedChannelData');
        }
      } else {
        localStorage.removeItem('cachedChannelData');
        localStorage.removeItem('lastChannelAnalysisDate');
      }
    }
  };

  const resetAnalysis = () => {
    dispatch({
      type: ActionTypes.RESET_ANALYSIS,
    });
  };

  const clearHistory = () => {
    localStorage.removeItem('lastChannelUrl');
    localStorage.removeItem('lastChannelAnalysisDate');
    localStorage.removeItem('cachedChannelData');

    dispatch({
      type: ActionTypes.CLEAR_HISTORY,
    });
  };

  const value = {
    ...state,
    setLoading,
    setError,
    setChannelUrl,
    setAnalysisData,
    resetAnalysis,
    clearHistory,
    setAutoLoadInProgress
  };

  return (
    <ChannelAnalysisContext.Provider value={value}>
      {children}
    </ChannelAnalysisContext.Provider>
  );
}

export function useChannelAnalysisContext() {
  const context = useContext(ChannelAnalysisContext);
  if (context === undefined) {
    throw new Error('useChannelAnalysisContext must be used within a ChannelAnalysisProvider');
  }
  return context;
}